// Render an NWS hourly forecast compactly enough to sit on a fixture row.
//
// NWS returns a free-text shortForecast ("Mostly Sunny", "Chance Rain Showers",
// "Patchy Fog then Sunny"). There is no code or icon id, so the phrase is
// matched to a glyph. Order matters: the checks run most-severe first, because
// "Chance Rain Showers then Sunny" is a rain forecast, not a sunny one.

const RULES = [
  [/thunder|tstorm/i, '⛈️', 'Storms'],
  [/snow|flurr|sleet|wintry|blizzard/i, '🌨️', 'Snow'],
  [/freezing|ice/i, '🧊', 'Ice'],
  [/rain|shower|drizzle/i, '🌧️', 'Rain'],
  [/fog|haze|smoke/i, '🌫️', 'Fog'],
  [/wind|breezy|blustery/i, '💨', 'Windy'],
  [/mostly cloudy|overcast/i, '☁️', 'Cloudy'],
  [/partly cloudy|partly sunny|mostly sunny|few clouds/i, '⛅', 'Partly cloudy'],
  [/cloud/i, '☁️', 'Cloudy'],
  [/clear|sunny|fair/i, '☀️', 'Clear'],
];

export function weatherGlyph(summary, isDaytime = true) {
  if (!summary) return { icon: '', label: '' };
  for (const [pattern, icon, label] of RULES) {
    if (pattern.test(summary)) {
      // A clear night is a moon, not a sun; everything else reads the same after
      // dark. MLS NEXT does schedule evening kickoffs.
      if (icon === '☀️' && !isDaytime) return { icon: '🌙', label };
      return { icon, label };
    }
  }
  return { icon: '', label: summary };
}

// Precipitation is only worth the space when it would change what someone
// packs; a 5% chance is noise on a crowded row. Thunderstorms are the exception
// — for those the number matters at any level, because it is the difference
// between a canopy and a cancelled game.
const PRECIP_THRESHOLD = 20;
const STORM_PRECIP_THRESHOLD = 1;

/**
 * A one-line summary for a fixture row: "☀️ 79°", or "⛈️ 80° 27%".
 */
export function formatWeather(w) {
  if (!w) return null;
  if (w.source === 'normal') return formatNormal(w);
  if (w.temperature == null) return null;
  const { icon } = weatherGlyph(w.summary, w.is_daytime !== false);
  const parts = [];
  if (icon) parts.push(icon);
  parts.push(`${Math.round(w.temperature)}°`);
  const stormy = /thunder|tstorm/i.test(w.summary || '');
  const floor = stormy ? STORM_PRECIP_THRESHOLD : PRECIP_THRESHOLD;
  if (typeof w.precipitation === 'number' && w.precipitation >= floor) {
    parts.push(`${w.precipitation}%`);
  }
  return parts.join(' ');
}

/**
 * A climate normal, deliberately not shaped like a forecast.
 *
 * Beyond the NWS horizon there is no forecast, so the server sends what that
 * date is usually like at that venue. It answers a different question — "what
 * should we pack" rather than "what will it be" — and must not be mistaken for
 * the other.
 *
 * Three things carry that distinction:
 *
 *   * The word "usually". Plain language beats a symbol; a tilde is a
 *     programmer's hedge and reads as noise on a fixture row.
 *   * A range rather than one number, because the question this answers is
 *     really about the spread — an 8am kickoff at 62° and a 2pm one at 78°
 *     need different bags.
 *   * No weather glyph, ever. The icon is now reserved for real forecasts, so
 *     its presence is the tell.
 *
 * The wet-year share is deliberately *not* here. On a forecast a percentage is
 * the chance of rain that day; on a normal it is the share of past years the
 * date was wet. Same position, same glyph, entirely different claim — so it
 * lives in the tooltip where there is room to say which it is.
 */
function formatNormal(w) {
  if (w.temperature == null) return null;
  const high = Math.round(w.temperature);
  if (w.temperature_low == null) return `usually ${high}°`;
  // En dash: a range, not a fraction. "62/78" reads as one over the other.
  return `usually ${Math.round(w.temperature_low)}\u2013${high}°`;
}

/** The fuller form, for a tooltip. */
export function describeWeather(w) {
  if (!w) return null;
  if (w.source === 'normal') {
    const bits = [];
    if (w.temperature != null) {
      const lo = w.temperature_low != null ? `, low ${Math.round(w.temperature_low)}°` : '';
      bits.push(`Typically ${Math.round(w.temperature)}°${w.unit || 'F'}${lo}`);
    }
    // Said in words, because the number alone would be read as a forecast's
    // chance of rain.
    if (typeof w.precipitation === 'number') bits.push(`wet in ${w.precipitation}% of years`);
    if (w.wind) bits.push(`wind ${w.wind}`);
    if (w.venue) bits.push(`at ${w.venue}`);
    const years = w.years ? `${w.years}-year average` : 'historical average';
    return `${bits.join(' · ')} (${years}, not a forecast)`;
  }
  const bits = [];
  if (w.summary) bits.push(w.summary);
  if (w.temperature != null) bits.push(`${Math.round(w.temperature)}°${w.unit || 'F'}`);
  if (typeof w.precipitation === 'number') bits.push(`${w.precipitation}% precipitation`);
  if (w.wind) bits.push(`wind ${w.wind}`);
  if (w.venue) bits.push(`at ${w.venue}`);
  return bits.join(' · ');
}
